import React, { useEffect, useState, useRef, Children, createElement } from 'react';
import { TrendingUpIcon, UsersIcon, ShieldCheckIcon, ActivityIcon, BarChart3Icon, NewspaperIcon, UtensilsIcon, BikeIcon, CoinsIcon, ArrowRightIcon, LightbulbIcon, SparklesIcon, TvIcon, DownloadIcon, CheckCircleIcon, WineIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine, Label, ReferenceArea } from 'recharts';
import { ConfidenceNotification } from './ConfidenceNotification';
import { ShareButton } from './ShareButton';
import { LearnMore } from './LearnMore';
import { MethodologyAccordion } from './MethodologyAccordion';
import { timelineData, audienceSegments, publishers, metrics, audiencePersonas } from '../utils/atlResonanceData';
export const ATLResonanceDashboard: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [activePhase, setActivePhase] = useState<'baseline' | 'flight' | 'post-flight' | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<{
    News: boolean;
    Food: boolean;
    Sport: boolean;
    Radio: boolean;
    Finance: boolean;
  }>({
    News: false,
    Food: false,
    Sport: false,
    Radio: false,
    Finance: false
  });
  const toggleCategory = (category: 'News' | 'Food' | 'Sport' | 'Radio' | 'Finance') => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-lg" data-id="element-4">
          <p className="text-sm font-semibold text-gray-900" data-id="element-5">{label}</p>
          <p className="text-sm text-[#1E90FF] mt-1" data-id="element-6">
            Index: <span className="font-bold" data-id="element-7">{payload[0].value}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-8">
            {payload[0].payload.phase}
          </p>
        </div>;
    }
    return null;
  };
  const downloadChart = async () => {
    if (!chartRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      const link = document.createElement('a');
      link.download = 'campaign-timeline.png';
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Failed to download chart:', error);
    }
  };
  return <motion.div className="space-y-8" variants={containerVariants} initial="hidden" animate="visible" data-id="element-9">
      {/* Header Section */}
      <div id="overview" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:mb-8" data-id="element-10">
        <div data-id="element-11">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-12">
            ATL Resonance Report: 50+ Mobiel
          </h1>
        </div>
        <div className="self-start sm:self-auto flex items-center bg-[#1E90FF]/10 text-[#1E90FF] px-4 py-2 rounded-lg print:hidden" data-id="element-13">
          <ActivityIcon className="h-5 w-5 mr-2" data-id="element-14" />
          <span className="text-sm font-semibold" data-id="element-15">
            Campaign Status: Completed
          </span>
        </div>
      </div>

      {/* Test Overview */}
      <motion.div variants={itemVariants} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300" data-id="element-16">
        <div className="flex flex-col md:flex-row items-start md:space-x-6 space-y-4 md:space-y-0" data-id="element-17">
          <img src="https://cdn.magicpatterns.com/uploads/4uL6wVwXu8pHj8KH4aPKBU/overview-icon.png" alt="Brandpulse" className="h-12 w-12 flex-shrink-0" data-id="element-18" />
          <div className="space-y-3 max-w-4xl" data-id="element-19">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900" data-id="element-20">
              Test Overview
            </h2>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-21">
              This report details the findings of an ATL Resonance test
              conducted by BrandPulse™ for 50+ Mobiel. We analyzed{' '}
              <span className="font-semibold text-gray-900" data-id="element-22">
                279,000+ Human Signals
              </span>{' '}
              across premium publishers. The conclusion is positive: We measured
              a validated uplift of{' '}
              <span className="font-semibold text-[#1E90FF]" data-id="element-23">+33%</span> that
              sustained even after the campaign stopped. Where traditional
              surveys ask a small group for their opinion, BrandPulse measures
              the actual behavior of the entire exposed population, providing
              certainty that the TV commercials successfully activated the
              market and drove measurable brand lift among the target
              demographic.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Key Takeaways Summary Box */}
      <motion.div variants={itemVariants} className="bg-gradient-to-r from-[#1E90FF]/5 to-blue-50/50 p-6 rounded-xl border-2 border-[#1E90FF]/20 print:bg-white print:border print:border-gray-300" data-id="element-24">
        <div className="flex items-center space-x-2 mb-4" data-id="element-25">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.3,
          type: 'spring'
        }} data-id="element-26">
            <CheckCircleIcon className="h-6 w-6 text-[#1E90FF]" data-id="element-27" />
          </motion.div>
          <h3 className="text-base md:text-lg font-semibold text-gray-900" data-id="element-28">
            Key Takeaways
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-3" data-id="element-29">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.4
        }} className="flex items-start space-x-2" data-id="element-30">
            <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-31" />
            <p className="text-sm text-gray-700" data-id="element-32">
              Campaign generated{' '}
              <span className="font-semibold text-gray-900" data-id="element-33">+33% uplift</span>{' '}
              in brand interest
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.5
        }} className="flex items-start space-x-2" data-id="element-34">
            <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-35" />
            <p className="text-sm text-gray-700" data-id="element-36">
              Effect sustained 2 weeks post-flight at{' '}
              <span className="font-semibold text-gray-900" data-id="element-37">+14%</span>
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.6
        }} className="flex items-start space-x-2" data-id="element-38">
            <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-39" />
            <p className="text-sm text-gray-700" data-id="element-40">
              Strongest with{' '}
              <span className="font-semibold text-gray-900" data-id="element-41">
                pension planning
              </span>{' '}
              audience (Index 250)
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.7
        }} className="flex items-start space-x-2" data-id="element-42">
            <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-43" />
            <p className="text-sm text-gray-700" data-id="element-44">
              <span className="font-semibold text-gray-900" data-id="element-45">
                97.5% lift certainty
              </span>{' '}
              that the campaign caused this result
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Info Blocks */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 print:gap-4" data-id="element-46">
        <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300" data-id="element-47">
          <div className="flex items-center space-x-2 mb-3" data-id="element-48">
            <BarChart3Icon className="h-5 w-5 text-[#1E90FF]" data-id="element-49" />
            <h3 className="text-sm md:text-base font-semibold text-gray-900" data-id="element-50">
              Test type
            </h3>
          </div>
          <p className="text-sm md:text-base text-gray-700" data-id="element-51">ATL Resonance</p>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300" data-id="element-52">
          <div className="flex items-center space-x-2 mb-3" data-id="element-53">
            <ActivityIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-54" />
            <h3 className="text-sm md:text-base font-semibold text-gray-900" data-id="element-55">
              Period
            </h3>
          </div>
          <p className="text-sm md:text-base text-gray-700" data-id="element-56">
            Dec 9, 2024 - Jan 7, 2025
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300" data-id="element-57">
          <div className="flex items-center space-x-2 mb-3" data-id="element-58">
            <ShieldCheckIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-59" />
            <h3 className="text-sm md:text-base font-semibold text-gray-900" data-id="element-60">
              Completion date
            </h3>
          </div>
          <p className="text-sm md:text-base text-gray-700" data-id="element-61">January 7, 2025</p>
        </motion.div>
      </div>

      {/* Commercial Video */}
      <motion.div variants={itemVariants} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300" data-id="element-62">
        <div className="flex items-center space-x-2 mb-6" data-id="element-63">
          <TvIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-64" />
          <h2 className="text-lg md:text-xl font-semibold text-gray-900" data-id="element-65">
            TV Commercial
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-66">
          <div className="w-full" data-id="element-67">
            <div className="relative w-full rounded-lg overflow-hidden bg-gray-100 shadow-md" style={{
            paddingBottom: '56.25%'
          }} data-id="element-68">
              <iframe className="absolute top-0 left-0 w-full h-full print:hidden" src="https://www.youtube.com/embed/PQAaRP0O5Uc" title="50+ Mobiel TV Commercial" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-69" />
              <div className="hidden print:flex absolute inset-0 items-center justify-center text-gray-500 text-sm" data-id="element-70">
                Video: 50+ Mobiel Campaign
              </div>
            </div>
          </div>

          <div className="space-y-4" data-id="element-71">
            <div data-id="element-72">
              <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2" data-id="element-73">
                50+ Mobiel: "Eindejaarsknallers"
              </h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-74">
                This TV commercial aired during the flight period (Dec 15 - Jan
                1) across major Dutch networks. The creative highlights an
                irresistible New Year deal: first 6 months for just 1 EUR with a
                2-year subscription, leaving you with money for all the fun
                things you want to do.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics Grid with Enhanced Tooltips */}
      <div id="metrics" className="grid grid-cols-1 md:grid-cols-3 gap-6 print:gap-4" data-id="element-75">
        <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300 flex flex-col" data-id="element-76">
          <div className="flex items-center space-x-2 mb-4" data-id="element-77">
            <UsersIcon className="h-5 w-5 text-gray-600" data-id="element-78" />
            <h3 className="text-sm md:text-base font-semibold text-gray-900" data-id="element-79">
              Human Signals Analyzed
            </h3>
          </div>
          <motion.p initial={{
          opacity: 0,
          scale: 0.5
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5,
          type: 'spring'
        }} className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-80">
            {metrics.humanSignals.toLocaleString()}
          </motion.p>
          <p className="text-sm text-gray-500 mb-2" data-id="element-81">
            Combined reach across publishers
          </p>

          <p className="text-xs text-gray-400 italic mb-4" data-id="element-82">
            In plain terms: Real people, not bots
          </p>

          <div className="mt-auto" data-id="element-83">
            <LearnMore title="What are Human Signals?" data-id="element-84">
              <p data-id="element-85">
                Human Signals represent verified interactions from real users
                engaging with content on premium publishers. Where traditional
                surveys ask a small group for their opinion ('Did you see this
                ad?'), BrandPulse measures the actual behavior of the entire
                exposed population. Each signal is validated to ensure it comes
                from genuine human behavior, not automated bots or fraudulent
                traffic. This gives you certainty that the measured uplift
                reflects actual audience response to your campaign.
              </p>
            </LearnMore>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border-2 border-[#1E90FF]/40 print:border print:border-[#1E90FF] flex flex-col" data-id="element-86">
          <div className="flex items-center space-x-2 mb-4" data-id="element-87">
            <TrendingUpIcon className="h-5 w-5 text-green-600" data-id="element-88" />
            <h3 className="text-sm md:text-base font-semibold text-gray-900" data-id="element-89">
              Resonance Score
            </h3>
          </div>
          <motion.p initial={{
          opacity: 0,
          scale: 0.5
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.6,
          type: 'spring'
        }} className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-90">
            +{metrics.uplift}%
          </motion.p>
          <p className="text-sm text-green-600 mb-2" data-id="element-91">
            ↑ VS baseline index (100)
          </p>

          <p className="text-xs text-gray-400 italic mb-4" data-id="element-92">
            In plain terms: 33% more brand interest than before
          </p>

          <div className="mt-auto" data-id="element-93">
            <LearnMore title="Understanding the Uplift" data-id="element-94">
              <p data-id="element-95">
                A +33% uplift means your campaign generated 33% more brand
                interest compared to the baseline period. This is measured
                through actual behavioral changes, increased engagement with
                brand-related content across premium publishers. We compare the
                Baseline Response Rate (Pre-Flight) against the Exposed Response
                Rate (During Flight) to calculate the pure behavioral lift. The
                baseline (Index 100) represents normal, pre-campaign interest
                levels, making this a significant positive impact.
              </p>
            </LearnMore>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300 flex flex-col" data-id="element-96">
          <div className="flex items-center space-x-2 mb-4" data-id="element-97">
            <ShieldCheckIcon className="h-5 w-5 text-gray-600" data-id="element-98" />
            <h3 className="text-sm md:text-base font-semibold text-gray-900" data-id="element-99">
              Lift Certainty
            </h3>
          </div>
          <motion.p initial={{
          opacity: 0,
          scale: 0.5
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.7,
          type: 'spring'
        }} className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-100">
            {metrics.confidence}%
          </motion.p>
          <p className="text-sm text-gray-500 mb-2" data-id="element-101">
            based on Bayesian Analysis
          </p>

          <p className="text-xs text-gray-400 italic mb-4" data-id="element-102">
            In plain terms: We're 97.5% certain your campaign caused this
          </p>

          <div className="mt-auto" data-id="element-103">
            <LearnMore title="What Does 97.5% Confidence Mean?" data-id="element-104">
              <p data-id="element-105">
                Using Bayesian statistical analysis, we can say with 97.5%
                certainty that your campaign caused the observed uplift, rather
                than random chance or external factors. This is a very high
                level of confidence, giving you strong evidence that your TV
                investment directly drove measurable results.
              </p>
            </LearnMore>
          </div>
        </motion.div>
      </div>

      {/* Timeline Visualization with Narrative Intro */}
      <motion.div id="timeline" variants={itemVariants} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300 print:break-inside-avoid" data-id="element-106">
        <div className="mb-4" data-id="element-107">
          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-108">
            <span className="font-semibold text-gray-700" data-id="element-109">
              Timeline Analysis:
            </span>{' '}
            Visualizing the correlation between media flight dates and brand
            interest peaks.
          </p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4" data-id="element-110">
          <div data-id="element-111">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900" data-id="element-112">
              The Heartbeat: Campaign Timeline
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-113">
              Visualizing user interest before, during, and after the TV flight
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-114">
            <button onClick={downloadChart} className="flex items-center space-x-2 text-sm text-gray-600 hover:text-[#1E90FF] transition-colors print:hidden self-start sm:self-auto" type="button" data-id="element-115">
              <DownloadIcon className="h-4 w-4" data-id="element-116" />
              <span data-id="element-117">Download</span>
            </button>
            <div className="flex flex-wrap items-center gap-4 text-sm print:space-x-3" data-id="element-118">
              <div className="flex items-center cursor-pointer transition-opacity duration-200 print:cursor-default" style={{
              opacity: activePhase === null || activePhase === 'baseline' ? 1 : 0.3
            }} onMouseEnter={() => setActivePhase('baseline')} onMouseLeave={() => setActivePhase(null)} data-id="element-119">
                <div className="w-3 h-3 bg-gray-300 rounded-full mr-2" data-id="element-120"></div>
                <span className="text-gray-600 font-medium" data-id="element-121">
                  Baseline (100)
                </span>
              </div>
              <div className="flex items-center cursor-pointer transition-opacity duration-200 print:cursor-default" style={{
              opacity: activePhase === null || activePhase === 'flight' ? 1 : 0.3
            }} onMouseEnter={() => setActivePhase('flight')} onMouseLeave={() => setActivePhase(null)} data-id="element-122">
                <div className="w-3 h-3 bg-[#1E90FF] rounded-full mr-2" data-id="element-123"></div>
                <span className="text-gray-600 font-medium" data-id="element-124">Flight (133)</span>
              </div>
              <div className="flex items-center cursor-pointer transition-opacity duration-200 print:cursor-default" style={{
              opacity: activePhase === null || activePhase === 'post-flight' ? 1 : 0.3
            }} onMouseEnter={() => setActivePhase('post-flight')} onMouseLeave={() => setActivePhase(null)} data-id="element-125">
                <div className="w-3 h-3 bg-blue-300 rounded-full mr-2" data-id="element-126"></div>
                <span className="text-gray-600 font-medium" data-id="element-127">
                  Post-Flight (114)
                </span>
              </div>
            </div>
          </div>
        </div>

        <div ref={chartRef} className="h-[300px] md:h-[400px] w-full relative print:h-[300px]" data-id="element-128">
          <div className="absolute inset-0 pointer-events-none z-10 print:hidden" data-id="element-129">
            <div className="h-full flex" data-id="element-130">
              <div className="pointer-events-auto cursor-pointer" style={{
              width: '20%'
            }} onMouseEnter={() => setActivePhase('baseline')} onMouseLeave={() => setActivePhase(null)} data-id="element-131" />
              <div className="pointer-events-auto cursor-pointer" style={{
              width: '56.67%'
            }} onMouseEnter={() => setActivePhase('flight')} onMouseLeave={() => setActivePhase(null)} data-id="element-132" />
              <div className="pointer-events-auto cursor-pointer" style={{
              width: '23.33%'
            }} onMouseEnter={() => setActivePhase('post-flight')} onMouseLeave={() => setActivePhase(null)} data-id="element-133" />
            </div>
          </div>

          <div className="absolute top-[58%] right-[8%] z-20 hidden md:block pointer-events-none" data-id="element-134">
            <span className="text-[#1E90FF] font-bold text-sm" data-id="element-135">
              +14% sustained
            </span>
          </div>

          <ResponsiveContainer width="100%" height="100%" data-id="element-136">
            <AreaChart data={timelineData} margin={{
            top: 20,
            right: 10,
            left: -10,
            bottom: 0
          }} data-id="element-137">
              <defs data-id="element-138">
                <linearGradient id="colorIndex" x1="0" y1="0" x2="0" y2="1" data-id="element-139">
                  <stop offset="5%" stopColor="#1E90FF" stopOpacity={0.3} data-id="element-140" />
                  <stop offset="95%" stopColor="#1E90FF" stopOpacity={0} data-id="element-141" />
                </linearGradient>
                <linearGradient id="colorIndexHighlight" x1="0" y1="0" x2="0" y2="1" data-id="element-142">
                  <stop offset="5%" stopColor="#1E90FF" stopOpacity={0.5} data-id="element-143" />
                  <stop offset="95%" stopColor="#1E90FF" stopOpacity={0.1} data-id="element-144" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-145" />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 11,
              fontWeight: 500
            }} dy={10} interval="preserveStartEnd" data-id="element-146" />
              <YAxis domain={[80, 160]} axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 11,
              fontWeight: 500
            }} label={{
              value: 'Interest Index',
              angle: -90,
              position: 'insideLeft',
              style: {
                fill: '#6B7280',
                fontSize: 11,
                fontWeight: 500
              }
            }} data-id="element-147" />
              <RechartsTooltip content={<CustomTooltip data-id="element-149" />} data-id="element-148" />

              {activePhase === 'baseline' && <ReferenceArea x1="Dec 9" x2="Dec 15" y1={80} y2={160} fill="#D1D5DB" fillOpacity={0.15} stroke="none" data-id="element-150" />}
              {activePhase === 'flight' && <ReferenceArea x1="Dec 15" x2="Jan 1" y1={80} y2={160} fill="#1E90FF" fillOpacity={0.12} stroke="none" data-id="element-151" />}
              {activePhase === 'post-flight' && <ReferenceArea x1="Jan 1" x2="Jan 7" y1={80} y2={160} fill="#93C5FD" fillOpacity={0.15} stroke="none" data-id="element-152" />}

              <ReferenceLine x="Dec 15" stroke="#9CA3AF" strokeDasharray="3 3" strokeWidth={1.5} data-id="element-153">
                <Label value="Launch" position="top" fill="#6B7280" fontSize={10} fontWeight={600} data-id="element-154" />
              </ReferenceLine>
              <ReferenceLine x="Jan 1" stroke="#9CA3AF" strokeDasharray="3 3" strokeWidth={1.5} data-id="element-155">
                <Label value="End" position="top" fill="#6B7280" fontSize={10} fontWeight={600} data-id="element-156" />
              </ReferenceLine>

              <ReferenceLine y={100} stroke="#EF4444" strokeDasharray="3 3" strokeWidth={1.5} data-id="element-157">
                <Label value="Baseline" position="insideTopRight" fill="#EF4444" fontSize={10} fontWeight={600} data-id="element-158" />
              </ReferenceLine>

              <ReferenceArea x1="Dec 15" x2="Dec 31" y1={100} y2={133} fill="transparent" stroke="none" data-id="element-159">
                <Label value="+33%" position="center" fill="#1E90FF" fontSize={16} fontWeight="bold" data-id="element-160" />
              </ReferenceArea>

              <ReferenceArea x1="Jan 1" x2="Jan 7" y1={100} y2={114} fill="transparent" stroke="none" data-id="element-161" />

              <Area type="monotone" dataKey="index" stroke="#1E90FF" strokeWidth={3} fillOpacity={1} fill={activePhase ? 'url(#colorIndexHighlight)' : 'url(#colorIndex)'} animationDuration={2000} baseValue={100} data-id="element-162" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 p-4 bg-blue-50/30 rounded-lg border border-blue-100 print:hidden" data-id="element-163">
          <div className="flex items-center space-x-2 mb-3" data-id="element-164">
            <LightbulbIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-165" />
            <h3 className="text-base md:text-lg font-semibold text-gray-900" data-id="element-166">
              Key Insights
            </h3>
          </div>

          <div className="space-y-3" data-id="element-167">
            {/* Brand Uplift During Flight */}
            <div className="p-3 bg-white rounded-lg border border-blue-200" data-id="element-168">
              <div className="flex items-start space-x-2" data-id="element-169">
                <TrendingUpIcon className="h-4 w-4 text-[#1E90FF] flex-shrink-0 mt-0.5" data-id="element-170" />
                <div data-id="element-171">
                  <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-172">
                    Brand Uplift During Flight: +33%
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-173">
                    The campaign generated a{' '}
                    <span className="font-semibold text-gray-900" data-id="element-174">
                      +33% uplift
                    </span>{' '}
                    in brand interest during the active flight period (Dec 15 -
                    Jan 1). This represents a significant increase in audience
                    engagement and validates the creative execution's
                    effectiveness in capturing attention.
                  </p>
                </div>
              </div>
            </div>

            {/* Instant Traction */}
            <div className="p-3 bg-gradient-to-r from-blue-50/50 to-cyan-50/30 rounded-lg border border-blue-200" data-id="element-175">
              <div className="flex items-start space-x-2" data-id="element-176">
                <SparklesIcon className="h-4 w-4 text-[#1E90FF] flex-shrink-0 mt-0.5" data-id="element-177" />
                <div data-id="element-178">
                  <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-179">
                    Instant Traction: Creative Validation
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-180">
                    We detected{' '}
                    <span className="font-semibold text-gray-900" data-id="element-181">
                      zero "warm-up period"
                    </span>
                    . The curve spiked immediately at the start of the flight.
                    This validates that the creative message
                    ("Eindejaarsknallers") was extremely clear and urgent to the
                    target audience, a strong compliment to the creative
                    execution.
                  </p>
                </div>
              </div>
            </div>

            {/* Brand Equity Dividend with Sustained Impact */}
            <div className="p-3 bg-gradient-to-r from-emerald-50/50 to-green-50/30 rounded-lg border border-emerald-200" data-id="element-182">
              <div className="flex items-start space-x-2" data-id="element-183">
                <CoinsIcon className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-184" />
                <div data-id="element-185">
                  <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-186">
                    Brand Equity Dividend: ROI of Retention
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-187">
                    The campaign delivered more than just temporary sales. We
                    see the brand lift also has a sustained effect for{' '}
                    <span className="font-semibold text-gray-900" data-id="element-188">2 weeks</span>{' '}
                    at <span className="font-semibold text-gray-900" data-id="element-189">+14%</span>{' '}
                    above baseline, which means that every future Euro we spend
                    starts from a higher efficiency level. This structural lift
                    represents a capital investment in brand value, not just a
                    marketing cost. The investment created lasting brand equity
                    that compounds over time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Statistical Proof & Audience Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:gap-4" data-id="element-190">
        {/* Statistical Proof */}
        <motion.div id="proof" variants={itemVariants} className="space-y-6 flex flex-col" data-id="element-191">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900" data-id="element-192">
            Statistical Proof
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-193">
            <div className="bg-[#1E90FF]/5 p-6 rounded-xl border border-[#1E90FF]/20 print:bg-white print:border print:border-gray-300" data-id="element-194">
              <div className="flex items-center space-x-3 mb-4" data-id="element-195">
                <ShieldCheckIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-196" />
                <h3 className="text-base font-semibold text-gray-900" data-id="element-197">
                  Lift Certainty
                </h3>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-2" data-id="element-198">97.5%</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2" data-id="element-199">
                Probability that the uplift was caused by the campaign and not
                by chance.
              </p>
            </div>
            <div className="bg-[#1E90FF]/5 p-6 rounded-xl border border-[#1E90FF]/20 print:bg-white print:border print:border-gray-300" data-id="element-200">
              <div className="flex items-center space-x-3 mb-4" data-id="element-201">
                <BarChart3Icon className="h-5 w-5 text-[#1E90FF]" data-id="element-202" />
                <h3 className="text-base font-semibold text-gray-900" data-id="element-203">
                  Statistical Validation
                </h3>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-2" data-id="element-204">
                p &lt; 0.05
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2" data-id="element-205">
                The uplift is statistically validated, not due to random chance.
              </p>
            </div>
          </div>

          {/* Premium Publisher Showcase - Enhanced */}
          <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300 flex-1" data-id="element-206">
            <div className="mb-6" data-id="element-207">
              <div className="flex items-center space-x-2 mb-3" data-id="element-208">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900" data-id="element-209">
                  Premium Measurement Environment
                </h2>
                <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.8,
                type: 'spring'
              }} data-id="element-210">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-211" />
                </motion.div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-212">
                Measurement took place at the heart of Dutch quality publishers.
                We see a stable presence on A-brands such as RTL, IEX, and major
                lifestyle titles, which guarantees that the measured resonance
                comes from real, engaged consumers.
              </p>
            </div>

            {/* Category Groups */}
            <div className="space-y-6" data-id="element-213">
              {/* News & Current Affairs */}
              <div data-id="element-214">
                <div className="flex items-center space-x-2 mb-3" data-id="element-215">
                  <div className="px-2.5 py-1 bg-[#1E90FF]/10 rounded-md" data-id="element-216">
                    <span className="text-xs font-bold text-[#1E90FF] uppercase tracking-wide" data-id="element-217">
                      News & Current Affairs
                    </span>
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-218">
                    Trusted Information
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-219">
                  {publishers.filter(pub => pub.category === 'News').slice(0, expandedCategories.News ? undefined : 2).map((pub, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.9 + idx * 0.1
                }} className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200 print:bg-white" data-id="element-220">
                        <div className="flex items-start justify-between mb-2" data-id="element-221">
                          <div className="flex items-center space-x-3" data-id="element-222">
                            <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 print:shadow-none" data-id="element-223">
                              <NewspaperIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-224" />
                            </div>
                            <div data-id="element-225">
                              <p className="text-sm font-bold text-gray-900" data-id="element-226">
                                {pub.name}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-227">
                                {pub.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>)}
                </div>
                {publishers.filter(pub => pub.category === 'News').length > 2 && <button onClick={() => toggleCategory('News')} className="mt-3 flex items-center space-x-1 text-sm text-[#1E90FF] hover:text-[#1a7ad9] transition-colors print:hidden" data-id="element-228">
                    <span data-id="element-229">
                      {expandedCategories.News ? 'Show less' : `Show ${publishers.filter(pub => pub.category === 'News').length - 2} more`}
                    </span>
                    {expandedCategories.News ? <ChevronUpIcon className="h-4 w-4" data-id="element-230" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-231" />}
                  </button>}
              </div>

              {/* Food & Lifestyle */}
              <div data-id="element-232">
                <div className="flex items-center space-x-2 mb-3" data-id="element-233">
                  <div className="px-2.5 py-1 bg-emerald-50 rounded-md" data-id="element-234">
                    <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide" data-id="element-235">
                      Food & Lifestyle
                    </span>
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-236">High Engagement</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-237">
                  {publishers.filter(pub => pub.category === 'Food').slice(0, expandedCategories.Food ? undefined : 2).map((pub, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 1.1 + idx * 0.1
                }} className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200 print:bg-white" data-id="element-238">
                        <div className="flex items-start justify-between mb-2" data-id="element-239">
                          <div className="flex items-center space-x-3" data-id="element-240">
                            <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 print:shadow-none" data-id="element-241">
                              <UtensilsIcon className="h-5 w-5 text-emerald-600" data-id="element-242" />
                            </div>
                            <div data-id="element-243">
                              <p className="text-sm font-bold text-gray-900" data-id="element-244">
                                {pub.name}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-245">
                                {pub.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>)}
                </div>
                {publishers.filter(pub => pub.category === 'Food').length > 2 && <button onClick={() => toggleCategory('Food')} className="mt-3 flex items-center space-x-1 text-sm text-emerald-700 hover:text-emerald-800 transition-colors print:hidden" data-id="element-246">
                    <span data-id="element-247">
                      {expandedCategories.Food ? 'Show less' : `Show ${publishers.filter(pub => pub.category === 'Food').length - 2} more`}
                    </span>
                    {expandedCategories.Food ? <ChevronUpIcon className="h-4 w-4" data-id="element-248" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-249" />}
                  </button>}
              </div>

              {/* Sport & Automotive */}
              <div data-id="element-250">
                <div className="flex items-center space-x-2 mb-3" data-id="element-251">
                  <div className="px-2.5 py-1 bg-amber-50 rounded-md" data-id="element-252">
                    <span className="text-xs font-bold text-amber-700 uppercase tracking-wide" data-id="element-253">
                      Sport & Automotive
                    </span>
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-254">Active 50+ Male</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-255">
                  {publishers.filter(pub => pub.category === 'Sport').slice(0, expandedCategories.Sport ? undefined : 2).map((pub, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 1.3 + idx * 0.1
                }} className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200 print:bg-white" data-id="element-256">
                        <div className="flex items-start justify-between mb-2" data-id="element-257">
                          <div className="flex items-center space-x-3" data-id="element-258">
                            <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 print:shadow-none" data-id="element-259">
                              <BikeIcon className="h-5 w-5 text-amber-600" data-id="element-260" />
                            </div>
                            <div data-id="element-261">
                              <p className="text-sm font-bold text-gray-900" data-id="element-262">
                                {pub.name}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-263">
                                {pub.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>)}
                </div>
                {publishers.filter(pub => pub.category === 'Sport').length > 2 && <button onClick={() => toggleCategory('Sport')} className="mt-3 flex items-center space-x-1 text-sm text-amber-700 hover:text-amber-800 transition-colors print:hidden" data-id="element-264">
                    <span data-id="element-265">
                      {expandedCategories.Sport ? 'Show less' : `Show ${publishers.filter(pub => pub.category === 'Sport').length - 2} more`}
                    </span>
                    {expandedCategories.Sport ? <ChevronUpIcon className="h-4 w-4" data-id="element-266" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-267" />}
                  </button>}
              </div>

              {/* Radio & Weather */}
              <div data-id="element-268">
                <div className="flex items-center space-x-2 mb-3" data-id="element-269">
                  <div className="px-2.5 py-1 bg-blue-50 rounded-md" data-id="element-270">
                    <span className="text-xs font-bold text-blue-700 uppercase tracking-wide" data-id="element-271">
                      Radio & Weather
                    </span>
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-272">
                    Contextual Relevance
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-273">
                  {publishers.filter(pub => pub.category === 'Radio').slice(0, expandedCategories.Radio ? undefined : 2).map((pub, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 1.5 + idx * 0.1
                }} className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200 print:bg-white" data-id="element-274">
                        <div className="flex items-start justify-between mb-2" data-id="element-275">
                          <div className="flex items-center space-x-3" data-id="element-276">
                            <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 print:shadow-none" data-id="element-277">
                              <ActivityIcon className="h-5 w-5 text-blue-600" data-id="element-278" />
                            </div>
                            <div data-id="element-279">
                              <p className="text-sm font-bold text-gray-900" data-id="element-280">
                                {pub.name}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-281">
                                {pub.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>)}
                </div>
                {publishers.filter(pub => pub.category === 'Radio').length > 2 && <button onClick={() => toggleCategory('Radio')} className="mt-3 flex items-center space-x-1 text-sm text-blue-700 hover:text-blue-800 transition-colors print:hidden" data-id="element-282">
                    <span data-id="element-283">
                      {expandedCategories.Radio ? 'Show less' : `Show ${publishers.filter(pub => pub.category === 'Radio').length - 2} more`}
                    </span>
                    {expandedCategories.Radio ? <ChevronUpIcon className="h-4 w-4" data-id="element-284" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-285" />}
                  </button>}
              </div>

              {/* Financial */}
              <div data-id="element-286">
                <div className="flex items-center space-x-2 mb-3" data-id="element-287">
                  <div className="px-2.5 py-1 bg-purple-50 rounded-md" data-id="element-288">
                    <span className="text-xs font-bold text-purple-700 uppercase tracking-wide" data-id="element-289">
                      Financial
                    </span>
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-290">
                    Affluent Audience
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-291">
                  {publishers.filter(pub => pub.category === 'Finance').slice(0, expandedCategories.Finance ? undefined : 2).map((pub, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 1.7 + idx * 0.1
                }} className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200 print:bg-white" data-id="element-292">
                        <div className="flex items-start justify-between mb-2" data-id="element-293">
                          <div className="flex items-center space-x-3" data-id="element-294">
                            <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 print:shadow-none" data-id="element-295">
                              <CoinsIcon className="h-5 w-5 text-purple-600" data-id="element-296" />
                            </div>
                            <div data-id="element-297">
                              <p className="text-sm font-bold text-gray-900" data-id="element-298">
                                {pub.name}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-299">
                                {pub.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>)}
                </div>
                {publishers.filter(pub => pub.category === 'Finance').length > 2 && <button onClick={() => toggleCategory('Finance')} className="mt-3 flex items-center space-x-1 text-sm text-purple-700 hover:text-purple-800 transition-colors print:hidden" data-id="element-300">
                    <span data-id="element-301">
                      {expandedCategories.Finance ? 'Show less' : `Show ${publishers.filter(pub => pub.category === 'Finance').length - 2} more`}
                    </span>
                    {expandedCategories.Finance ? <ChevronUpIcon className="h-4 w-4" data-id="element-302" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-303" />}
                  </button>}
              </div>
            </div>

            {/* Show More/Less Button */}
          </div>
        </motion.div>

        {/* Audience Insights Column - Combined */}
        <motion.div id="audience" variants={itemVariants} className="space-y-6 flex flex-col" data-id="element-304">
          {/* Audience Resonance */}
          <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300" data-id="element-305">
            <div className="mb-6" data-id="element-306">
              <p className="text-sm text-gray-500 leading-relaxed mb-4" data-id="element-307">
                <span className="font-semibold text-gray-700" data-id="element-308">
                  Audience Resonance:
                </span>{' '}
                Identifying which demographic and interest segments reacted most
                strongly to the campaign stimulus.
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-id="element-309">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900" data-id="element-310">
                  Audience Resonance
                </h2>
                <span className="self-start sm:self-auto text-xs font-semibold text-[#1E90FF] bg-[#1E90FF]/10 px-3 py-1.5 rounded-full" data-id="element-311">
                  Who responded?
                </span>
              </div>
            </div>

            <div className="space-y-5" data-id="element-312">
              {audienceSegments.map((segment, idx) => <div key={idx} className="relative" data-id="element-313">
                  <div className="flex items-center justify-between mb-2" data-id="element-314">
                    <div className="flex items-center space-x-2" data-id="element-315">
                      <span className="text-sm font-semibold text-gray-900" data-id="element-316">
                        {segment.name}
                      </span>
                      {idx === 0 && <span className="text-[10px] font-bold text-white bg-[#1E90FF] px-2 py-0.5 rounded-full" data-id="element-317">
                          TOP SEGMENT
                        </span>}
                    </div>
                    <span className="text-sm font-bold text-[#1E90FF]" data-id="element-318">
                      Index: {segment.index}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden" data-id="element-319">
                    <motion.div className="h-2.5 rounded-full bg-[#1E90FF]" initial={{
                  width: 0
                }} animate={{
                  width: `${segment.index / 250 * 100}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5 + idx * 0.1
                }} data-id="element-320" />
                  </div>
                  <div className="flex items-center justify-between mt-1.5" data-id="element-321">
                    <p className="text-xs text-gray-500 font-medium" data-id="element-322">
                      {segment.index > 100 ? `${segment.index - 100}% above average interest` : 'Average interest'}
                    </p>
                    <p className="text-xs text-gray-400 italic" data-id="element-323">
                      {segment.index > 100 && `${(segment.index / 100).toFixed(1)}x more interested`}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-100 print:bg-white" data-id="element-324">
              <div className="flex items-start space-x-3" data-id="element-325">
                <ArrowRightIcon className="h-5 w-5 text-[#1E90FF] mt-0.5 flex-shrink-0" data-id="element-326" />
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-327">
                  <span className="font-semibold text-gray-900" data-id="element-328">Insight:</span>{' '}
                  The campaign resonated most strongly with users actively
                  interested in{' '}
                  <span className="font-semibold text-[#1E90FF]" data-id="element-329">
                    Pension Planning
                  </span>{' '}
                  (Index 250), confirming the relevance for the 50+ demographic.
                </p>
              </div>
            </div>
          </div>

          {/* Audience Interests Revealed - Moved here */}
          <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300 flex-1" data-id="element-330">
            <div className="mb-6" data-id="element-331">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3" data-id="element-332">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900" data-id="element-333">
                  Audience Interests Revealed
                </h2>
                <span className="self-start sm:self-auto text-xs font-semibold text-[#1E90FF] bg-[#1E90FF]/10 px-3 py-1.5 rounded-full" data-id="element-334">
                  Who are they?
                </span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-335">
                {' '}
                Beyond "50+ Mobiel" as a broad demographic, behavioral data
                reveals three distinct sub-profiles based on actual content
                engagement patterns.
              </p>
            </div>

            <div className="space-y-4" data-id="element-336">
              {audiencePersonas.map((persona, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.8 + idx * 0.15,
              type: 'spring'
            }} className="relative bg-gray-50 p-5 rounded-lg border border-gray-200" data-id="element-337">
                  {/* Index Badge */}
                  <div className="absolute top-4 right-4" data-id="element-338">
                    <div className="px-2.5 py-1 rounded-full bg-[#1E90FF]/10 border border-[#1E90FF]/20" data-id="element-339">
                      <span className="text-xs font-bold text-[#1E90FF]" data-id="element-340">
                        Index {persona.index}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4" data-id="element-341">
                    {/* Icon */}
                    <div className="flex-shrink-0 p-2.5 rounded-lg bg-white border border-gray-200" data-id="element-342">
                      {persona.icon === 'wine' && <WineIcon className="h-5 w-5 text-gray-700" data-id="element-343" />}
                      {persona.icon === 'newspaper' && <NewspaperIcon className="h-5 w-5 text-gray-700" data-id="element-344" />}
                      {persona.icon === 'coins' && <CoinsIcon className="h-5 w-5 text-gray-700" data-id="element-345" />}
                    </div>

                    <div className="flex-1 pr-12 md:pr-16" data-id="element-346">
                      {/* Title */}
                      <h3 className="text-base font-bold text-gray-900 mb-1" data-id="element-347">
                        {persona.name}
                      </h3>
                      <p className="text-xs font-medium mb-2 text-gray-600" data-id="element-348">
                        {persona.tagline}
                      </p>

                      {/* Description */}
                      <p className="text-xs text-gray-600 leading-relaxed mb-3" data-id="element-349">
                        {persona.description}
                      </p>

                      {/* Interests */}
                      <div className="flex flex-wrap gap-1.5" data-id="element-350">
                        {persona.interests.slice(0, 3).map((interest, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-700" data-id="element-351">
                            {interest}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* Strategic Insight */}
            <div className="mt-6 p-4 bg-[#1E90FF]/5 rounded-lg border border-[#1E90FF]/20" data-id="element-352">
              <div className="flex items-start space-x-3" data-id="element-353">
                <LightbulbIcon className="h-4 w-4 text-[#1E90FF] flex-shrink-0 mt-0.5" data-id="element-354" />
                <div data-id="element-355">
                  <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-356">
                    Strategic Implication
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-357">
                    The strongest resonance lies with{' '}
                    <span className="font-semibold text-gray-900" data-id="element-358">
                      The Smart Investor
                    </span>{' '}
                    (Index 250) and{' '}
                    <span className="font-semibold text-gray-900" data-id="element-359">
                      The Bon Vivant
                    </span>{' '}
                    (Index 185). Future campaigns should position 50+ Mobiel as
                    the smart choice that frees up money for what matters most,
                    whether that's securing your family's financial future
                    through investments and planning, or enjoying the lifestyle
                    you've earned through travel and experiences. This audience
                    values financial intelligence: they're not just saving
                    money, they're strategically reallocating it toward their
                    priorities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Key Insight Section */}
      <motion.div variants={itemVariants} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300 print:break-inside-avoid" data-id="element-360">
        <div className="flex flex-col md:flex-row items-start justify-between gap-4 mb-6" data-id="element-361">
          <div className="flex items-start space-x-4" data-id="element-362">
            <LightbulbIcon className="h-6 w-6 text-[#1E90FF] flex-shrink-0 mt-1" data-id="element-363" />
            <h2 className="text-lg md:text-xl font-semibold text-gray-900" data-id="element-364">
              Key Insight: TV Built Lasting Brand Equity, Not Just Temporary
              Awareness
            </h2>
          </div>
          <ShareButton title="Key Insight: TV Built Lasting Brand Equity, Not Just Temporary Awareness" text="The 50+ Mobiel campaign didn't just drive a +33% spike—it created a sustained +14% lift that persisted weeks after the campaign ended. This represents a structural investment in brand value that compounds over time, with 97.5% certainty." data-id="element-365" />
        </div>

        <div className="space-y-4 text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-366">
          <p data-id="element-367">
            This test validates TV as a{' '}
            <span className="font-semibold text-gray-900" data-id="element-368">
              brand-building engine
            </span>
            , not just an awareness channel. The campaign generated a{' '}
            <span className="font-semibold text-gray-900" data-id="element-369">
              +33% uplift during flight
            </span>
            , but the real story is the{' '}
            <span className="font-semibold text-gray-900" data-id="element-370">
              sustained +14% post-flight effect
            </span>
            , proof that you've built lasting brand equity. This structural lift
            means every future Euro spent starts from a higher efficiency level.
            It's not just marketing cost; it's{' '}
            <span className="font-semibold text-gray-900" data-id="element-371">
              capital investment in brand value
            </span>{' '}
            that compounds over time.
          </p>

          <p data-id="element-372">
            The data reveals two critical insights: First, we detected{' '}
            <span className="font-semibold text-gray-900" data-id="element-373">
              zero "warm-up period"
            </span>
            , the curve spiked immediately at campaign launch. This validates
            that the creative message ("Eindejaarsknallers") was extremely clear
            and urgent to the target audience. Second, the strongest resonance
            came from{' '}
            <span className="font-semibold text-gray-900" data-id="element-374">
              The Smart Investor persona
            </span>{' '}
            (Index 250), users actively researching pension planning and
            financial optimization. This audience isn't just saving money;
            they're strategically reallocating it toward their priorities.
          </p>

          <p data-id="element-375">
            With{' '}
            <span className="font-semibold text-gray-900" data-id="element-376">
              97.5% lift certainty
            </span>{' '}
            and statistical validation (p &lt; 0.05), we can confidently say
            this campaign didn't just drive temporary interest, it fundamentally
            shifted how the 50+ market perceives 50+ Mobiel. The strategic
            opportunity now is to maintain this elevated baseline while
            optimizing creative and frequency to maximize ROI on future flights.
          </p>
        </div>
      </motion.div>

      {/* Recommendations & Next Steps */}
      <motion.div variants={itemVariants} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-300 print:break-inside-avoid" data-id="element-377">
        <div className="flex items-start space-x-4 mb-8" data-id="element-378">
          <SparklesIcon className="h-6 w-6 text-[#1E90FF] flex-shrink-0 mt-1" data-id="element-379" />
          <h2 className="text-lg md:text-xl font-semibold text-gray-900" data-id="element-380">
            Recommendations & Next Steps
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 print:gap-4" data-id="element-381">
          <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100 print:bg-white print:border print:border-gray-300" data-id="element-382">
            <div className="flex items-center space-x-3 mb-4" data-id="element-383">
              <div className="p-2 bg-white rounded-lg shadow-sm print:shadow-none" data-id="element-384">
                <CoinsIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-385" />
              </div>
              <h3 className="text-base font-semibold text-gray-900" data-id="element-386">
                Protect Brand Equity
              </h3>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-387">
              Maintain consistent TV presence to preserve the +14% elevated
              baseline. Set up continuous measurement to track brand equity
              evolution and optimize maintenance frequency.
            </p>
          </div>

          <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100 print:bg-white print:border print:border-gray-300" data-id="element-388">
            <div className="flex items-center space-x-3 mb-4" data-id="element-389">
              <div className="p-2 bg-white rounded-lg shadow-sm print:shadow-none" data-id="element-390">
                <UsersIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-391" />
              </div>
              <h3 className="text-base font-semibold text-gray-900" data-id="element-392">
                Target Smart Investor
              </h3>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-393">
              Focus on The Smart Investor persona (Index 250) with messaging
              that positions 50+ Mobiel as financially intelligent. Test
              persona-specific creative variants in Q2.
            </p>
          </div>

          <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100 print:bg-white print:border print:border-gray-300" data-id="element-394">
            <div className="flex items-center space-x-3 mb-4" data-id="element-395">
              <div className="p-2 bg-white rounded-lg shadow-sm print:shadow-none" data-id="element-396">
                <SparklesIcon className="h-5 w-5 text-[#1E90FF]" data-id="element-397" />
              </div>
              <h3 className="text-base font-semibold text-gray-900" data-id="element-398">
                Optimize Flight Strategy
              </h3>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-399">
              The zero warm-up period suggests opportunity to test shorter, more
              frequent bursts vs. sustained flights to maximize efficiency and
              ROI.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 print:bg-white print:border print:border-gray-300" data-id="element-400">
          <p className="text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-401">
            This test proves TV created a{' '}
            <span className="font-semibold text-gray-900" data-id="element-402">
              structural investment in brand value
            </span>
            , not just temporary awareness. The sustained +14% post-flight
            effect represents brand equity that compounds over time. With 97.5%
            certainty, protect this gain through consistent presence, amplify
            what works by targeting The Smart Investor persona, and optimize
            efficiency through strategic testing.
          </p>
          <div className="flex items-center justify-end mt-4" data-id="element-403">
            <div className="flex items-center space-x-2 text-sm" data-id="element-404">
              <ShieldCheckIcon className="h-4 w-4 text-[#1E90FF]" data-id="element-405" />
              <span className="text-gray-600 font-medium" data-id="element-406">
                {metrics.confidence}% Lift Certainty
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Methodology Accordion */}
      <MethodologyAccordion data-id="element-407" />

      {/* Confidence Notification */}
      <ConfidenceNotification uplift={metrics.uplift} confidence={metrics.confidence} data-id="element-408" />
    </motion.div>;
};